import { useCallback } from "react";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValueAnswer } from "@tellescope/types-models";
import { parseTimeAnswer } from "../../utils";

/**
 * Returns a resolver that reads a field's configured `options.default` into a value of the right
 * shape for its type, returning undefined when there is no usable default.
 */
export const useResolveSeededDefault = () => {
  const { format } = useDateFnsFormattersWithLocale();

  return useCallback(
    (field: TellescopeModelFromName<"form_fields">): FormResponseValueAnswer["value"] => {
      const configuredDefault = field.options?.default;
      const stringDefault =
        typeof configuredDefault === "string" && configuredDefault !== "" ? configuredDefault : undefined;

      switch (field.type) {
        case "string":
        case "stringLong":
        case "email":
        case "phone":
        case "Timezone":
          return typeof configuredDefault === "string" ? configuredDefault : undefined;
        case "number": {
          if (typeof configuredDefault === "number") return configuredDefault;
          if (stringDefault === undefined) return undefined;
          const parsed = Number(stringDefault);
          return Number.isNaN(parsed) ? undefined : parsed;
        }
        case "date": {
          if (stringDefault === undefined) return undefined;
          const parsed = new Date(stringDefault);
          return Number.isNaN(parsed.getTime()) ? undefined : parsed;
        }
        case "dateString":
          if (stringDefault !== undefined) return stringDefault;
          return field.options?.prefillCurrentDate ? format(new Date(), "MM-dd-yyyy") : undefined;
        case "Time":
          return stringDefault === undefined ? undefined : parseTimeAnswer(stringDefault)?.local;
        case "multiple_choice":
        case "Dropdown":
          if (Array.isArray(configuredDefault)) return configuredDefault;
          return stringDefault === undefined ? undefined : [stringDefault];
        default:
          return undefined;
      }
    },
    [format]
  );
};
